import { Controller } from '@hotwired/stimulus';

const STORAGE_KEY = 'csp_reporter';
const TTL = 24 * 60 * 60 * 1000; // 24h
const MAX_REPORTS_PER_DAY = 20;
const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 30_000; // 30s

const IGNORED_BLOCKED_URIS = [
  'inline',
  'eval',
  'wasm-eval',
  'wasm-unsafe-eval',
  'data',
  'blob',
];
const IGNORED_SOURCE_PATTERNS = [
  /^chrome-extension/,
  /^moz-extension/,
  /^safari-extension/,
];

export default class extends Controller {
  connect() {
    this.buffer = [];
    this.handleViolation = this.handleViolation.bind(this);
    this.flush = this.flush.bind(this);
    this.handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') this.flush();
    };

    document.addEventListener('securitypolicyviolation', this.handleViolation);
    this.flushTimer = setInterval(this.flush, FLUSH_INTERVAL);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  disconnect() {
    document.removeEventListener('securitypolicyviolation', this.handleViolation);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    clearInterval(this.flushTimer);
    this.flush();
  }

  handleViolation(event) {
    const report = {
      'blocked-uri': event.blockedURI,
      'document-uri': event.documentURI,
      'violated-directive': event.violatedDirective,
      'source-file': event.sourceFile,
      'line-number': event.lineNumber,
    };

    if (this.#ignorable(report)) return;

    const state = this.#loadState();
    const fingerprint = this.#fingerprint(report);

    if (state.reported.includes(fingerprint)) return;
    if (state.count >= MAX_REPORTS_PER_DAY) return;

    state.reported.push(fingerprint);
    state.count += 1;
    this.#saveState(state);

    this.buffer.push(report);

    if (this.buffer.length >= BATCH_SIZE) this.flush();
  }

  flush() {
    if (this.buffer.length === 0) return;

    const reports = this.buffer.splice(0);

    fetch('/csp_violation_reports', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 'csp-reports': reports }),
      keepalive: true,
    }).catch(() => {});
  }

  #ignorable(report) {
    const blocked = report['blocked-uri'] || '';
    const source = report['source-file'] || '';

    return (
      IGNORED_BLOCKED_URIS.includes(blocked) ||
      IGNORED_SOURCE_PATTERNS.some((p) => p.test(source))
    );
  }

  #fingerprint(report) {
    return `${report['violated-directive']}:${report['blocked-uri']}`;
  }

  #loadState() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);

      if (raw) {
        const state = JSON.parse(raw);

        if (Date.now() - state.timestamp < TTL) return state;
      }
    } catch {
      // corrupted state, reset
    }

    return { reported: [], count: 0, timestamp: Date.now() };
  }

  #saveState(state) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // localStorage full or unavailable
    }
  }
}
